// src/pages/public/RoadmapPage.jsx
import { useState, useRef, useEffect } from 'react'

// ── Data ─────────────────────────────────────────────────────────────────────
const MILESTONES = [
  {
    id: 'M1',
    number: '01',
    title: 'Foundation & Trust Infrastructure',
    subtitle: 'Core platform hardening, verification, and dispute systems',
    timeline: 'Months 1–6',
    color: '#FF3D7F',
    glow: 'rgba(255,61,127,0.3)',
    bg: 'rgba(255,61,127,0.08)',
    status: 'In Progress',
    statusColor: '#10B981',
    kpis: [
      { value: '80%+', label: 'On-time dispatch' },
      { value: '<24h', label: 'Dispute resolution' },
      { value: '4.2+', label: 'Avg supplier rating' },
      { value: '60%+', label: 'Repeat buyer rate' },
    ],
    features: [
      { icon: '✅', name: 'Supplier Verification System', desc: 'GST/MSME/GI document verification with trust badges. Verified suppliers earn 3× conversion.', priority: 'Critical' },
      { icon: '⏱', name: 'Order SLA Tracking', desc: 'Auto-demotion in search after 3 dispatch violations. Real-time SLA monitoring.', priority: 'Critical' },
      { icon: '🔒', name: 'Buyer Protection Guarantee', desc: 'Escrow-style payment hold. Funds released only after confirmed delivery.', priority: 'Critical' },
      { icon: '⭐', name: 'Review & Rating Overhaul', desc: 'Verified purchase reviews with photo/video support. Supplier response capability.', priority: 'High' },
      { icon: '🛡', name: 'Dispute Resolution Engine', desc: 'SLA-bound escalation workflow. Auto-refund trigger on resolution timeout.', priority: 'High' },
      { icon: '📱', name: 'WhatsApp Notification Layer', desc: 'Order updates, RFQ replies, payment confirmations via WhatsApp Business API.', priority: 'High' },
      { icon: '📊', name: 'Supplier Performance Dashboard', desc: 'Public scorecard: On-time %, return rate, avg response time, and review score.', priority: 'Medium' },
    ],
    why: 'Every other feature depends on buyers trusting a supplier they have never met. Without this, conversion rates stay below 5%.',
    dependency: null,
  },
  {
    id: 'M2',
    number: '02',
    title: 'Data Moat & Network Effects',
    subtitle: 'Fabric database, community, trend reports, and swatch subscription',
    timeline: 'Months 4–10',
    color: '#E11D74',
    glow: 'rgba(225,29,116,0.3)',
    bg: 'rgba(225,29,116,0.08)',
    status: 'Planned',
    statusColor: '#F59E0B',
    kpis: [
      { value: '500K+', label: 'Fabrics indexed' },
      { value: '1,000+', label: 'Swatch subscribers' },
      { value: '50K+', label: 'Monthly Index reads' },
      { value: '2,000+', label: 'Brand/designer buyers' },
    ],
    features: [
      { icon: '🗄', name: 'Structured Fabric Database', desc: '50+ attribute indexing per product: GSM, weave, composition, origin, GI tag. The world\'s most complete textile catalogue.', priority: 'Critical' },
      { icon: '📦', name: 'Physical Swatch Subscription', desc: 'Designers/brands pay ₹2,999/month for curated swatches with QR codes linking back to Fabrinza.', priority: 'High' },
      { icon: '📈', name: 'Fabrinza Textile Index', desc: 'Monthly public trend report on demand signals and price movements. Press coverage, SEO authority.', priority: 'High' },
      { icon: '🎨', name: 'Designer & Brand Network', desc: 'Separate buyer tier for D2C brands, fashion designers, garment exporters. High AOV, high repeat.', priority: 'High' },
      { icon: '🏷', name: 'GI Tag Premium Marketplace', desc: 'Kanjivaram, Banarasi, Pochampally — certified GI section with premium pricing and international shipping.', priority: 'High' },
      { icon: '📊', name: 'Supplier Credit Score', desc: 'Proprietary scoring model on GMV, completion rate, returns. License to banks and NBFCs.', priority: 'Medium' },
      { icon: '📡', name: 'Trend API (Beta)', desc: 'Internal API for trend data. Pre-launch for monetisation in M6.', priority: 'Medium' },
    ],
    why: 'The data collected here is the training corpus for the M3 AI layer. Start early, collect obsessively.',
    dependency: 'M1 product listings & transactions',
  },
  {
    id: 'M3',
    number: '03',
    title: 'AI & Intelligence Layer',
    subtitle: 'Fabric recognition, demand forecasting, smart pricing, design assistant',
    timeline: 'Months 8–16',
    color: '#FF8500',
    glow: 'rgba(255,133,0,0.3)',
    bg: 'rgba(255,133,0,0.08)',
    status: 'Planned',
    statusColor: '#F59E0B',
    kpis: [
      { value: '85%+', label: 'Recognition accuracy' },
      { value: '3×', label: 'Search conversion lift' },
      { value: '₹999/mo', label: 'Forecasting subscription' },
      { value: '<5%', label: 'Fraud rate' },
    ],
    features: [
      { icon: '📸', name: 'Fabric Recognition AI', desc: 'Upload any fabric photo → instant supplier matches. The "Shazam for textiles." Biggest differentiation vs all competitors.', priority: 'Critical' },
      { icon: '📅', name: 'Demand Forecasting Engine', desc: 'Predict what to produce 90 days ahead based on order patterns, seasons, and fashion weeks. Sold as supplier intelligence.', priority: 'Critical' },
      { icon: '💡', name: 'Smart Pricing Engine', desc: 'Real-time price suggestions based on cotton/silk commodity rates, competitor listings, and live demand signals.', priority: 'High' },
      { icon: '✏', name: 'AI Design Assistant', desc: 'Buyer types a design brief in natural language → AI generates reference designs + matches to capable suppliers.', priority: 'High' },
      { icon: '🔍', name: 'AI-Powered Semantic Search', desc: '"Show me light summer saree fabric in pastel" returns perfectly relevant results — no keywords needed.', priority: 'High' },
      { icon: '⚠', name: 'Fraud Detection ML', desc: 'Real-time fraud scoring on orders, accounts, and reviews. Feeds back into M1 trust layer continuously.', priority: 'Medium' },
    ],
    why: 'AI requires labelled fabric data from M2 and 6–12 months of order patterns. Attempting AI before the data moat produces low-quality models.',
    dependency: 'M2 fabric database + 6 months of order history',
  },
  {
    id: 'M4',
    number: '04',
    title: 'Fabrinza Finance',
    subtitle: 'Working capital loans, invoice discounting, BNPL, and insurance',
    timeline: 'Months 12–20',
    color: '#DC7600',
    glow: 'rgba(220,118,0,0.3)',
    bg: 'rgba(220,118,0,0.08)',
    status: 'Planned',
    statusColor: '#F59E0B',
    kpis: [
      { value: '₹200 Cr', label: 'Lending revenue (Yr 5)' },
      { value: '₹40 Cr', label: 'Insurance premium (Yr 5)' },
      { value: '₹30 Cr', label: 'Credit API revenue (Yr 5)' },
      { value: '18%', label: 'Working capital rate' },
    ],
    features: [
      { icon: '🏦', name: 'Working Capital Loans', desc: 'Supplier gets a ₹5L order → needs ₹2L for yarn → Fabrinza Finance lends it at 18% pa. Repaid from order proceeds.', priority: 'Critical' },
      { icon: '📄', name: 'Invoice Discounting', desc: 'Suppliers discount unpaid buyer invoices for immediate cash. Fabrinza earns the discount spread.', priority: 'Critical' },
      { icon: '🛡', name: 'Embedded Cargo Insurance', desc: '₹99 per shipment. Partner with Digit/Acko. Auto-triggered on dispatch, zero paperwork.', priority: 'High' },
      { icon: '💳', name: 'BNPL for Buyers', desc: 'Fabric-specific Buy Now Pay Later for verified D2C brands. 30/60-day credit lines.', priority: 'High' },
      { icon: '📊', name: 'Credit Score API', desc: 'License Fabrinza supplier credit scores to banks and NBFCs for textile sector lending. Pure recurring revenue.', priority: 'High' },
      { icon: '💱', name: 'Forex & Export Payments', desc: 'Auto-convert international buyer payments. Integrate with AD-Category II bank partner.', priority: 'Medium' },
    ],
    why: 'Lenders require 12+ months of repayment data, AI credit scores (M3), and GST-verified identities (M1). Attempting lending before M1–M3 results in high NPAs.',
    dependency: 'M3 credit scores + M1 GST verification + 12 months history',
  },
  {
    id: 'M5',
    number: '05',
    title: 'Vertical Integration',
    subtitle: 'Logistics, quality assurance, ERP Lite, and upstream procurement',
    timeline: 'Months 16–28',
    color: '#3B82F6',
    glow: 'rgba(59,130,246,0.3)',
    bg: 'rgba(59,130,246,0.08)',
    status: 'Planned',
    statusColor: '#F59E0B',
    kpis: [
      { value: '₹80 Cr', label: 'Logistics margin (Yr 5)' },
      { value: '₹60 Cr', label: 'QA + Insurance (Yr 5)' },
      { value: '5×', label: 'AOV increase with ERP' },
      { value: '95%', label: 'ERP supplier retention' },
    ],
    features: [
      { icon: '🚚', name: 'Fabrinza Logistics Network', desc: 'White-label Delhivery/XpressBees. Charge ₹8/metre, earn the spread. Real-time tracking, cold-chain for silk.', priority: 'Critical' },
      { icon: '🔬', name: 'Quality Assurance Network', desc: 'Physical inspection agents. ₹500/order QA. Certified "Fabrinza Verified" tag on passed orders.', priority: 'Critical' },
      { icon: '🏭', name: 'Garment Manufacturer Module', desc: 'Extend upstream: fabric + trims + labels + packaging in one flow. 5× order value increase per buyer.', priority: 'High' },
      { icon: '📱', name: 'Fabrinza ERP Lite (Free)', desc: 'Free inventory, orders, GST filing for suppliers. Shopify strategy — own the entire business stack.', priority: 'High' },
      { icon: '🔌', name: 'White-Label B2B Ordering', desc: 'FabIndia, Manyavar run supplier procurement through Fabrinza infra under their own brand. Enterprise SaaS.', priority: 'Medium' },
      { icon: '♻', name: 'Reverse Logistics & Returns', desc: 'Structured returns with QA inspection, grading, and resale or recycling flow.', priority: 'Medium' },
    ],
    why: 'ERP Lite is the most important retention tool. Once a supplier runs their entire business on Fabrinza, switching costs become existential — not just inconvenient.',
    dependency: 'M4 cash flow + M3 AI optimisation + M2 supplier density',
  },
  {
    id: 'M6',
    number: '06',
    title: 'International Expansion',
    subtitle: 'Export facilitation, global buyer marketplace, GI premium exports',
    timeline: 'Months 24–36',
    color: '#8B5CF6',
    glow: 'rgba(139,92,246,0.3)',
    bg: 'rgba(139,92,246,0.08)',
    status: 'Planned',
    statusColor: '#F59E0B',
    kpis: [
      { value: '₹150 Cr', label: 'Export facilitation (Yr 5)' },
      { value: '40%+', label: 'GI premium margin' },
      { value: '₹40 Cr', label: 'Trend API (Yr 5)' },
      { value: 'UAE, UK, USA', label: 'Priority markets' },
    ],
    features: [
      { icon: '🌐', name: 'Export Facilitation Suite', desc: 'DGFT documentation, foreign buyer discovery, LC management, shipping consolidation. 4% of export GMV.', priority: 'Critical' },
      { icon: '🏪', name: 'Global Buyer Marketplace', desc: 'Indian suppliers listed to buyers in UAE, UK, USA, Southeast Asia. Multilingual AI handles discovery.', priority: 'Critical' },
      { icon: '💎', name: 'GI Premium Export Channel', desc: 'Kanjivaram, Banarasi, Pochampally to luxury global buyers. 40–60% margins vs commodity 5–10%.', priority: 'Critical' },
      { icon: '📊', name: 'Trend API Commercial Launch', desc: 'Sell real-time textile trend data to fashion brands, investors, and commodity traders internationally.', priority: 'High' },
      { icon: '🏢', name: 'Sourcing Agent Network', desc: 'Certified Fabrinza sourcing agents in Dubai, London, New York. Represent suppliers at global trade fairs.', priority: 'High' },
      { icon: '📋', name: 'Compliance Automation', desc: 'Auto-generate RCMC, COO, phytosanitary, ATA carnet exports. Saves 10+ hours per international shipment.', priority: 'Medium' },
    ],
    why: 'International expansion requires domestic logistics (M5), cross-border payment rails (M4), GI-certified products (M2), and multilingual AI (M3). All four must be live first.',
    dependency: 'M4 forex + M5 logistics + M3 multilingual AI + M2 GI database',
  },
  {
    id: 'M7',
    number: '07',
    title: 'Platform OS — Ecosystem Plays',
    subtitle: 'Developer API, third-party integrations, and industry infrastructure',
    timeline: 'Months 30–48',
    color: '#1A1A2E',
    glow: 'rgba(26,26,46,0.4)',
    bg: 'rgba(26,26,46,0.06)',
    status: 'Vision',
    statusColor: '#8B5CF6',
    kpis: [
      { value: '₹120 Cr', label: 'SaaS revenue (Yr 5)' },
      { value: '1,000+', label: 'API developer accounts' },
      { value: '$1.2B', label: 'Target valuation' },
      { value: '#1', label: 'Textile industry OS' },
    ],
    features: [
      { icon: '⚙', name: 'Fabrinza Open API', desc: 'Public REST API for inventory sync, order placement, price feeds, fabric search. Developers build Shopify/Unicommerce plugins.', priority: 'Critical' },
      { icon: '🔌', name: 'ERP Integrations', desc: 'Pre-built connectors for Tally, Zoho, SAP Business One, QuickBooks. Larger suppliers come because it talks to their systems.', priority: 'Critical' },
      { icon: '🏛', name: 'Industry Consortium Seat', desc: 'Co-found a textile body with SIMA, CITI. Shape regulations. Gives Fabrinza a permanent political moat.', priority: 'High' },
      { icon: '📚', name: 'Fabrinza Academy', desc: 'Free upskilling for suppliers: digital commerce, export documentation, design software. Organic discovery engine.', priority: 'High' },
      { icon: '🤖', name: 'AI Procurement Agents', desc: 'Enterprise buyers configure agents that autonomously sample, compare, order, and reorder fabric by rules.', priority: 'High' },
      { icon: '🌱', name: 'Sustainability Certification', desc: 'GOTS, OEKO-TEX, carbon footprint labelling. Required for EU export. Premium pricing and ESG investor appeal.', priority: 'Medium' },
    ],
    why: 'You can only build a platform ecosystem once you own enough of the industry workflow that third-party developers have strong incentive to build on top of you.',
    dependency: 'M1–M6 all live + dominant market position',
  },
  {
    id: 'M8',
    number: '08',
    title: 'Multi-Tenant SaaS Platform',
    subtitle: 'White-label Fabrinza as a service — sell the entire platform to other marketplaces',
    timeline: 'Months 20–36',
    color: '#059669',
    glow: 'rgba(5,150,105,0.3)',
    bg: 'rgba(5,150,105,0.08)',
    status: 'Planned',
    statusColor: '#059669',
    kpis: [
      { value: '₹45K/mo', label: 'Growth plan price' },
      { value: '30 sec', label: 'Tenant provisioning' },
      { value: '10×', label: 'Valuation multiple (ARR)' },
      { value: '3 billing', label: 'Models: Sub / RevShare / Hybrid' },
    ],
    features: [
      { icon: '🏗', name: 'Model A Shared DB Tenancy', desc: 'Single database, tenant_id column on every table. Global Eloquent scope on all models — zero cross-tenant leakage. Battle-tested Spatie package used for tenant resolution.', priority: 'Critical' },
      { icon: '🌐', name: 'Subdomain & Custom Domain', desc: 'rajasthantextiles.fabrinza.com auto-provisioned. Custom domains (marketplace.tnweavers.gov.in) verified via DNS TXT record.', priority: 'Critical' },
      { icon: '⚡', name: 'Tenant Provisioning in 30s', desc: 'TenantProvisioningService creates tenant, admin user, subdomain, seeds 5 CMS pages, and emails credentials automatically.', priority: 'Critical' },
      { icon: '🎨', name: 'Per-Tenant Theming Engine', desc: 'React TenantContext fetches brand config at boot, injects CSS variables (--primary, --font-heading, --logo). Every component automatically reflects the tenant brand.', priority: 'Critical' },
      { icon: '💳', name: 'Option 2 — Tiered Subscriptions', desc: 'Starter ₹15K/mo (50 suppliers), Growth ₹45K/mo (500 suppliers, custom domain, analytics), Enterprise ₹1.5L/mo (unlimited, API, dedicated SLA). Razorpay Subscriptions.', priority: 'Critical' },
      { icon: '🏛', name: 'Option 1 — Revenue Share (NGO/Gov)', desc: '0 monthly fee, 1.5% of GMV. For government textile boards, NGOs, and state GI portals who have budget constraints but high transaction volume.', priority: 'High' },
      { icon: '🔀', name: 'Option 3 — Hybrid (Future)', desc: 'Reduced monthly fee + small transaction %. Best of both worlds. Introduced at Series A when GMV data exists to price it correctly.', priority: 'High' },
      { icon: '🛡', name: 'Tenant Isolation Security', desc: 'TenantScope appends WHERE tenant_id=? to every query. BelongsToTenant trait auto-stamps on create. Automated cross-tenant leak tests in CI pipeline.', priority: 'Critical' },
      { icon: '⚙', name: 'Tenant Admin Portal', desc: 'Full white-label admin: branding editor, custom domain management, team invites, billing, and marketplace analytics — all scoped to their tenant data.', priority: 'High' },
      { icon: '📊', name: 'Super Admin Console', desc: 'Platform-level tenant list, provisioning, plan management, cross-tenant GMV analytics, MRR trend, suspend/unsuspend, and one-click impersonation.', priority: 'High' },
    ],
    why: 'A single-tenant marketplace is valued at 1–3× GMV. A multi-tenant SaaS platform is valued at 8–15× ARR. 10 tenants at ₹45K/month = ₹54L ARR and a fundamentally different investor story.',
    dependency: 'M1 trust layer (buyers trust the platform) + M3 AI catalogue (what tenants sell) + M5 logistics (fulfilment rails tenants need)',
  },
]

const REVENUE = [
  { stream: 'Marketplace Commission (3.5%)', milestone: 'M1', yr5: '₹350 Cr', color: '#FF3D7F' },
  { stream: 'Fabrinza Finance — Lending & BNPL', milestone: 'M4', yr5: '₹200 Cr', color: '#DC7600' },
  { stream: 'Export Facilitation (4% GMV)', milestone: 'M6', yr5: '₹150 Cr', color: '#8B5CF6' },
  { stream: 'SaaS / White-Label / ERP', milestone: 'M5/M7', yr5: '₹120 Cr', color: '#3B82F6' },
  { stream: 'Logistics Margin', milestone: 'M5', yr5: '₹80 Cr', color: '#3B82F6' },
  { stream: 'QA + Insurance', milestone: 'M5', yr5: '₹60 Cr', color: '#10B981' },
  { stream: 'Data, Trend API, Credit Score', milestone: 'M2/M3', yr5: '₹40 Cr', color: '#FF8500' },
  { stream: 'Multi-Tenant SaaS — Subscription ARR', milestone: 'M8', yr5: '₹54 Cr+', color: '#059669' },
]

const PRIORITY_COLORS = {
  Critical: { bg: '#FEF2F2', text: '#DC2626', border: '#FCA5A5' },
  High:     { bg: '#FFF7ED', text: '#D97706', border: '#FCD34D' },
  Medium:   { bg: '#EFF6FF', text: '#2563EB', border: '#93C5FD' },
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function RoadmapPage() {
  const [activeM, setActiveM] = useState('M1')
  const [hoveredFeature, setHoveredFeature] = useState(null)
  const [visibleMs, setVisibleMs] = useState(new Set(['M1']))
  const timelineRef = useRef(null)

  // Intersection observer for scroll animations
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) setVisibleMs(v => new Set([...v, e.target.dataset.id]))
      }),
      { threshold: 0.1 }
    )
    document.querySelectorAll('[data-milestone]').forEach(el => obs.observe(el))
    return () => obs.disconnect()
  }, [])

  const active = MILESTONES.find(m => m.id === activeM)

  return (
    <div style={{ fontFamily: '"DM Sans", sans-serif', background: '#FAFAFA', minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,700;0,800;1,700&family=Syne:wght@600;700;800&display=swap');

        :root {
          --pink: #FF3D7F;
          --orange: #FF8500;
          --dark: #1A1A2E;
        }

        .rm-hero { background: linear-gradient(135deg, #1A1A2E 0%, #2D1B4E 50%, #1A1A2E 100%); position: relative; overflow: hidden; }
        .rm-hero::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255,61,127,0.18) 0%, transparent 70%); pointer-events: none; }
        .rm-hero::after  { content: ''; position: absolute; inset: 0; background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E"); pointer-events: none; }

        .rm-title { font-family: 'Playfair Display', serif; font-size: clamp(2.4rem, 5vw, 4rem); font-weight: 800; line-height: 1.1; color: #fff; }
        .rm-title span { background: linear-gradient(135deg, #FF3D7F, #FF8500); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }

        .timeline-dot { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: 'Syne', sans-serif; font-weight: 800; font-size: 0.75rem; flex-shrink: 0; transition: all 0.3s; cursor: pointer; border: 2.5px solid transparent; }
        .timeline-dot:hover { transform: scale(1.1); }
        .timeline-dot.active { transform: scale(1.15); box-shadow: 0 0 0 6px rgba(255,61,127,0.15); }

        .milestone-card { border-radius: 20px; border: 1.5px solid rgba(0,0,0,0.07); background: #fff; transition: all 0.35s cubic-bezier(0.4,0,0.2,1); cursor: pointer; }
        .milestone-card:hover { border-color: var(--c); box-shadow: 0 8px 40px var(--glow); transform: translateY(-2px); }
        .milestone-card.active { border-color: var(--c); box-shadow: 0 12px 48px var(--glow); }

        .feature-item { border-radius: 14px; border: 1px solid #F0F0F0; background: #FAFAFA; padding: 14px 16px; transition: all 0.2s; cursor: default; }
        .feature-item:hover { border-color: var(--c); background: var(--bg); transform: translateX(4px); }

        .kpi-chip { border-radius: 14px; background: linear-gradient(135deg, var(--c), var(--c2)); padding: 16px 20px; text-align: center; }

        .progress-bar { height: 6px; border-radius: 3px; background: #F0F0F0; overflow: hidden; }
        .progress-fill { height: 100%; border-radius: 3px; background: linear-gradient(90deg, var(--pink), var(--orange)); transition: width 1s ease; }

        .revenue-row { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px dashed #F0F0F0; }
        .revenue-row:last-child { border-bottom: none; }

        .status-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 20px; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.03em; }

        .dep-arrow { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 10px; background: rgba(255,61,127,0.06); border: 1px dashed rgba(255,61,127,0.25); font-size: 0.8rem; color: #FF3D7F; font-weight: 600; }

        .slide-in { opacity: 0; transform: translateY(30px); transition: all 0.6s cubic-bezier(0.4,0,0.2,1); }
        .slide-in.visible { opacity: 1; transform: translateY(0); }

        .valuation-banner { background: linear-gradient(135deg, #1A1A2E 0%, #2D1B4E 100%); border-radius: 24px; overflow: hidden; position: relative; }
        .valuation-banner::before { content: ''; position: absolute; inset: 0; background: radial-gradient(circle at 80% 50%, rgba(255,61,127,0.25), transparent 60%); }

        @keyframes pulse-glow { 0%,100% { box-shadow: 0 0 20px rgba(255,61,127,0.3); } 50% { box-shadow: 0 0 40px rgba(255,61,127,0.6); } }
        .pulse { animation: pulse-glow 2s ease-in-out infinite; }

        @media (max-width: 768px) {
          .rm-layout { flex-direction: column; }
          .rm-timeline-col { display: none; }
        }
      `}</style>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <div className="rm-hero" style={{ padding: '80px 40px 60px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,61,127,0.15)', border: '1px solid rgba(255,61,127,0.3)', borderRadius: 30, padding: '6px 16px', marginBottom: 24 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF3D7F', display: 'inline-block' }} className="pulse" />
            <span style={{ color: '#FF3D7F', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em' }}>PRODUCT ROADMAP 2025–2030</span>
          </div>

          <h1 className="rm-title">
            The Path to<br /><span>₹1,000 Crore</span><br />& Beyond
          </h1>

          <p style={{ color: 'rgba(255,255,255,0.55)', marginTop: 20, fontSize: '1.05rem', maxWidth: 560, lineHeight: 1.65, fontWeight: 400 }}>
            7 dependency-ordered milestones. 36 features. 48 months. From India's most trusted textile marketplace to the industry's complete operating system.
          </p>

          {/* Hero stats */}
          <div style={{ display: 'flex', gap: 24, marginTop: 40, flexWrap: 'wrap' }}>
            {[
              { value: '7', label: 'Milestones' },
              { value: '36', label: 'Features' },
              { value: '48 mo', label: 'Timeline' },
              { value: '$1.2B', label: 'Target Valuation' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.8rem', background: 'linear-gradient(135deg, #FF3D7F, #FF8500)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{s.value}</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', fontWeight: 500, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ─────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px' }}>

        {/* Dependency chain */}
        <div style={{ marginBottom: 48 }}>
          <p style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 16 }}>Dependency Chain</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', paddingBottom: 8 }}>
            {MILESTONES.map((m, i) => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <button
                  onClick={() => setActiveM(m.id)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '8px 12px',
                    background: activeM === m.id ? m.bg : 'transparent',
                    border: `2px solid ${activeM === m.id ? m.color : '#E5E7EB'}`,
                    borderRadius: 14, cursor: 'pointer', transition: 'all 0.2s', minWidth: 80,
                  }}
                >
                  <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '0.9rem', color: m.color }}>{m.id}</span>
                  <span style={{ fontSize: '0.62rem', color: '#6B7280', fontWeight: 600, textAlign: 'center', lineHeight: 1.3 }}>{m.timeline}</span>
                </button>
                {i < MILESTONES.length - 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px' }}>
                    <div style={{ width: 20, height: 2, background: 'linear-gradient(90deg, #E5E7EB, #FF3D7F22)' }} />
                    <span style={{ color: '#D1D5DB', fontSize: '0.75rem' }}>›</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Active milestone detail */}
        {active && (
          <div key={activeM} style={{ marginBottom: 56, animation: 'fadeIn 0.4s ease' }}>
            <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 28, flexWrap: 'wrap' }}>
              <div style={{ width: 64, height: 64, borderRadius: 18, background: `linear-gradient(135deg, ${active.color}, ${active.color}99)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 8px 24px ${active.glow}` }}>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.2rem', color: '#fff' }}>{active.id}</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                  <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.6rem', fontWeight: 800, color: '#1A1A2E', margin: 0 }}>{active.title}</h2>
                  <span className="status-badge" style={{ background: `${active.statusColor}15`, color: active.statusColor, border: `1px solid ${active.statusColor}40` }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: active.statusColor, display: 'inline-block' }} />
                    {active.status}
                  </span>
                </div>
                <p style={{ color: '#6B7280', margin: '0 0 8px', fontSize: '0.95rem' }}>{active.subtitle}</p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ background: `${active.color}15`, color: active.color, border: `1px solid ${active.color}30`, borderRadius: 20, padding: '3px 12px', fontSize: '0.78rem', fontWeight: 700 }}>⏱ {active.timeline}</span>
                  {active.dependency && (
                    <span className="dep-arrow">
                      🔗 Needs: {active.dependency}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Why */}
            <div style={{ background: `linear-gradient(135deg, ${active.bg}, ${active.bg}88)`, border: `1px solid ${active.color}25`, borderRadius: 14, padding: '14px 18px', marginBottom: 28 }}>
              <span style={{ fontWeight: 700, fontSize: '0.8rem', color: active.color }}>WHY THIS ORDER  </span>
              <span style={{ color: '#374151', fontSize: '0.9rem' }}>{active.why}</span>
            </div>

            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 28 }}>
              {active.kpis.map(k => (
                <div key={k.label} style={{ borderRadius: 14, border: `1.5px solid ${active.color}25`, background: '#fff', padding: '16px 18px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.4rem', color: active.color }}>{k.value}</div>
                  <div style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 500, marginTop: 4 }}>{k.label}</div>
                </div>
              ))}
            </div>

            {/* Features grid */}
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 14 }}>Features in this Milestone</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
              {active.features.map((f, i) => {
                const pc = PRIORITY_COLORS[f.priority]
                return (
                  <div key={f.name} className="feature-item"
                    style={{ '--c': active.color, '--bg': active.bg }}
                    onMouseEnter={() => setHoveredFeature(`${activeM}-${i}`)}
                    onMouseLeave={() => setHoveredFeature(null)}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <span style={{ fontSize: '1.3rem', flexShrink: 0, marginTop: 1 }}>{f.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                          <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1A1A2E' }}>{f.name}</span>
                          <span style={{ background: pc.bg, color: pc.text, border: `1px solid ${pc.border}`, borderRadius: 10, padding: '2px 8px', fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}>{f.priority}</span>
                        </div>
                        <p style={{ color: '#6B7280', fontSize: '0.8rem', lineHeight: 1.55, margin: 0 }}>{f.desc}</p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* All milestones overview */}
        <div style={{ marginBottom: 56 }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.6rem', color: '#1A1A2E', marginBottom: 6 }}>All Milestones</h2>
          <p style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: 24 }}>Click any card to explore features in detail.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {MILESTONES.map((m, i) => (
              <div
                key={m.id}
                data-milestone data-id={m.id}
                className={`milestone-card ${activeM === m.id ? 'active' : ''}`}
                style={{ '--c': m.color, '--glow': m.glow, padding: '20px 22px', cursor: 'pointer' }}
                onClick={() => { setActiveM(m.id); window.scrollTo({ top: 0, behavior: 'smooth' }); setTimeout(() => document.querySelector('.dep-arrow')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100) }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, ${m.color}, ${m.color}bb)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${m.glow}` }}>
                    <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '0.8rem', color: '#fff' }}>{m.id}</span>
                  </div>
                  <span className="status-badge" style={{ background: `${m.statusColor}15`, color: m.statusColor, border: `1px solid ${m.statusColor}40`, fontSize: '0.65rem' }}>
                    {m.status}
                  </span>
                </div>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#1A1A2E', margin: '0 0 6px' }}>{m.title}</h3>
                <p style={{ color: '#9CA3AF', fontSize: '0.78rem', lineHeight: 1.5, margin: '0 0 14px' }}>{m.timeline} · {m.features.length} features</p>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: m.status === 'In Progress' ? '35%' : m.status === 'Planned' ? '0%' : '0%' }} />
                </div>
                {m.dependency && (
                  <p style={{ color: '#D1D5DB', fontSize: '0.7rem', marginTop: 8, fontStyle: 'italic' }}>Needs: {m.dependency.split('+')[0].trim()}…</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Revenue model */}
        <div style={{ marginBottom: 56 }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.6rem', color: '#1A1A2E', marginBottom: 6 }}>5-Year Revenue Model</h2>
          <p style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: 28 }}>All streams compound to ~₹1,000 Cr by Year 5 — supporting a $1.2B valuation at standard B2B marketplace multiples.</p>
          <div style={{ background: '#fff', borderRadius: 20, border: '1.5px solid #F0F0F0', padding: '24px 28px', marginBottom: 20 }}>
            {REVENUE.map((r, i) => {
              const pct = parseInt(r.yr5.replace(/[^0-9]/g, ''))
              const maxPct = 350
              const bar = Math.round((pct / maxPct) * 100)
              return (
                <div key={r.stream} className="revenue-row">
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', truncate: true }}>{r.stream}</span>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
                        <span style={{ background: `${r.color}15`, color: r.color, fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>{r.milestone}</span>
                        <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '0.9rem', color: '#1A1A2E', minWidth: 80, textAlign: 'right' }}>{r.yr5}</span>
                      </div>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${bar}%`, background: `linear-gradient(90deg, ${r.color}, ${r.color}99)` }} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Valuation banner */}
          <div className="valuation-banner" style={{ padding: '32px 36px' }}>
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
              <div>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 6px' }}>Total Year 5 Revenue</p>
                <div style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '2.4rem', background: 'linear-gradient(135deg, #FF3D7F, #FF8500)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>~₹1,000 Crore</div>
                <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.82rem', marginTop: 6 }}>@ 10× revenue multiple = $1.2B valuation</p>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {[['Faire', '$12B', 'USA B2B wholesale'], ['Udaan', '$3.1B', 'India B2B commerce'], ['Zetwerk', '$2.7B', 'India manufacturing']].map(([n, v, d]) => (
                  <div key={n} style={{ textAlign: 'center', padding: '12px 16px', background: 'rgba(255,255,255,0.06)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#FF8500' }}>{v}</div>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.78rem', fontWeight: 700 }}>{n}</div>
                    <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem', marginTop: 2 }}>{d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Closing quote */}
        <div style={{ textAlign: 'center', padding: '40px 24px', borderTop: '1px solid #F0F0F0' }}>
          <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.15rem', fontStyle: 'italic', color: '#374151', maxWidth: 640, margin: '0 auto 16px', lineHeight: 1.65 }}>
            "The companies that built operating systems for vertical industries became the most defensible businesses of their era. Fabrinza's opportunity in Indian textiles is exactly that."
          </p>
          <p style={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 600 }}>— Fabrinza Product Strategy, 2025</p>
        </div>

      </div>
    </div>
  )
}
